import { useSyncExternalStore } from 'react';

/**
 * Subscribe to a CSS media query and re-render when its match state flips.
 *
 * Backed by `useSyncExternalStore` so the value is always read fresh from
 * `matchMedia` (no tearing, no setState-in-effect). SSR-safe: the server
 * snapshot is `false`, so callers should treat "no match" as the pre-hydration
 * default. In this CSR app hydration isn't a concern, but the guard keeps the
 * hook portable.
 *
 * @example
 * const isDesktop = useMediaQuery('(min-width: 640px)');
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => subscribe(query, onChange),
    () => getSnapshot(query),
    () => false,
  );
}

function subscribe(query: string, onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return noop;
  }
  const mql = window.matchMedia(query);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

/** No-op unsubscribe returned when `matchMedia` isn't available (SSR). */
function noop(): void {
  // nothing to clean up — no listener was attached
}

function getSnapshot(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(query).matches;
}
